import { useRef, useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'u', 's', 'a', 'ul', 'ol', 'li', 'p', 'br', 'div'];
const ALLOWED_ATTR = ['href', 'title'];
const ALLOWED_URI_REGEXP = /^https?:\/\//i;

function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
}

/**
 * Hook encapsulating all contentEditable rich text editor logic:
 * sanitization, format tracking, keyboard shortcuts, paste handling,
 * and value sync.
 */
function useRichTextEditor({
 value, onChange, onTriggerLink,
}) {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState(new Set());

  // Update which formatting is active at cursor position
  const updateActiveFormats = () => {
    const formats = new Set();

    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('insertUnorderedList')) formats.add('bullet');
    if (document.queryCommandState('insertOrderedList')) formats.add('number');

    // Bold/italic/lists are handled by queryCommandState above (reflects the pending-toggle state,
    // so toggling Bold off updates the button even at a <b> boundary). Only walk ancestors for <A>.
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      let node = selection.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'A') {
          formats.add('link');
          break;
        }
        node = node.parentNode;
      }
    }

    setActiveFormats(formats);
  };

  // Sync value prop to innerHTML — skip while typing to avoid cursor jumping,
  // but always sync when value is cleared (e.g. after send).
  useEffect(() => {
    const sanitizedHtml = sanitizeHtml(value);

    // Cleared (e.g. after send) — no cursor event fires, so reset the toolbar or buttons stay highlighted.
    if (!sanitizedHtml) {
      setActiveFormats((prev) => (prev.size ? new Set() : prev));
    }

    if (!editorRef.current) return;
    const hasFocus = editorRef.current.contains(document.activeElement);

    if (!hasFocus || !sanitizedHtml) {
      if (editorRef.current.innerHTML !== sanitizedHtml) {
        editorRef.current.innerHTML = sanitizedHtml || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (!editorRef.current) return;
    const sanitized = sanitizeHtml(editorRef.current.innerHTML);
    onChange({ target: { value: sanitized } });
    updateActiveFormats();
  };

  const applyFormat = (command, urlValue = null) => {
    document.execCommand(command, false, urlValue);
    editorRef.current?.focus();
    handleInput();
  };

  const toggleBold = () => applyFormat('bold');
  const toggleItalic = () => applyFormat('italic');
  const toggleBulletList = () => applyFormat('insertUnorderedList');
  const toggleNumberedList = () => applyFormat('insertOrderedList');

  const insertLink = (url, text) => {
    editorRef.current?.focus();
    const safeText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    const safeUrl = DOMPurify.sanitize(url, { ALLOWED_TAGS: [] });
    const linkHtml = `<a href="${safeUrl}">${safeText}</a>`;
    document.execCommand('insertHTML', false, linkHtml);
    handleInput();
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
      e.preventDefault();
      toggleBold();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
      e.preventDefault();
      toggleItalic();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      onTriggerLink?.();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  const isEmpty = !value || value === '' || value === '<br>' || value === '<div><br></div>';

  const handleBlur = () => {
    if (isEmpty && editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  return {
    editorRef,
    activeFormats,
    isEmpty,
    applyFormat,
    toggleBold,
    toggleItalic,
    toggleBulletList,
    toggleNumberedList,
    insertLink,
    updateActiveFormats,
    editorProps: {
      onInput: handleInput,
      onKeyDown: handleKeyDown,
      onKeyUp: updateActiveFormats,
      onClick: updateActiveFormats,
      onMouseUp: updateActiveFormats,
      onPaste: handlePaste,
      onBlur: handleBlur,
    },
  };
}

export default useRichTextEditor;
